/* import __COLOCATED_TEMPLATE__ from './qualification-funnel.hbs'; */
/* RESPONSIBLE TEAM: team-automation-foundations */
/* eslint-disable @intercom/intercom/no-bare-strings */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import type { Options, SeriesOptionsType, SeriesSankeyOptions, Point } from 'highcharts';
import { use } from 'ember-resources/util/function-resource';
import {
  SdrQualificationFunnelConfig,
  STATUS_SERIES_LOOKUP_KEYS,
} from 'embercom/components/reporting/bespoke/sdr-performance/qualification-funnel/series-config';
import PALETTE from '@intercom/pulse/lib/palette';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import { staticDataResource } from 'embercom/lib/reporting/static-data-resource';
import type { SeriesSankeyPointOptionsObject } from 'highcharts';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type ChartSeries from 'embercom/models/reporting/custom/chart-series';
import type RenderableChart from 'embercom/models/reporting/custom/renderable-chart';
import { task } from 'ember-concurrency-decorators';
import { type TaskGenerator } from 'ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';
import { get } from 'embercom/lib/ajax';

export const SANKEY_COLORS = {
  channelType: PALETTE['vis-blue-40'],
  sdrParticipated: PALETTE['vis-blue-50'],
  capturedContact: PALETTE['vis-mint-50'],
  deadEnd: PALETTE['text-disabled'],
  default: PALETTE['neutral-border-emphasis'],
  recovery: PALETTE['vis-green-50'],
  escalated: PALETTE['vis-orange-50'],
  disqualified: PALETTE['vis-pink-50'],
};

// Color palette for routing outcomes
const OUTCOME_COLORS = [
  PALETTE['vis-blue-50'],
  PALETTE['vis-azure-50'],
  PALETTE['vis-pink-50'],
  PALETTE['vis-orange-50'],
  PALETTE['vis-yellow-50'],
  PALETTE['vis-mint-50'],
  PALETTE['vis-teal-50'],
  PALETTE['vis-cyan-50'],
];

const CHANNEL_TYPES = ['Chat', 'Email'];

// Static translation key mapping to avoid dynamic translation keys
const CHANNEL_TRANSLATION_KEYS: Record<string, string> = {
  Chat: 'ai-agent.analyze.sales-performance.funnel-metrics.Chat',
  Email: 'ai-agent.analyze.sales-performance.funnel-metrics.Email',
};

type OptionsWithSankey = Options & { plotOptions: { sankey: { borderRadius: number } } };

function stripZeroWeights(
  data: SeriesSankeyPointOptionsObject[],
): SeriesSankeyPointOptionsObject[] {
  return data.filter((point) => point.weight !== 0);
}

interface FunnelPoint extends Point {
  title?: string;
  findByLabel?: string;
  weight?: number;
  count?: number;
}

type RoutingOutcome = {
  identifier: string;
  title: string;
  workflow: any;
  fin_instruction_text: string;
  predicates: any[];
  required_memory_fields: any[];
  optional_memory_fields: any[];
};

type SdrConfig = {
  app_id: number;
  routing_outcomes: RoutingOutcome[];
  general_instructions: any;
  integrations: any;
};

interface Signature {
  Args: {
    renderableChart: RenderableChart;
    openUnderlyingDataModal: (chartSeries: ChartSeries) => void;
  };
  Blocks: {
    default: [];
  };
  Element: null;
}

export default class QualificationFunnel extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare appService: AppService;

  @tracked tooltipOpenerElement: HTMLElement | undefined = undefined;
  @tracked hoveredPoint: FunnelPoint | null = null;
  @tracked finSdrConfig: SdrConfig | null = null;
  @tracked configLoaded = false;

  constructor(owner: any, args: Signature['Args']) {
    super(owner, args);
    taskFor(this.loadFinSdrConfig).perform();
  }

  @task({ restartable: true })
  *loadFinSdrConfig(): TaskGenerator<void> {
    try {
      let appId = this.appService.app.id;
      let response = (yield get(`/teammate_app/fin_sdr_config?app_id=${appId}`)) as SdrConfig;
      this.finSdrConfig = response;
    } catch (error) {
      console.error('[SDR Funnel] Failed to load SDR config:', error);
      this.finSdrConfig = null;
    } finally {
      this.configLoaded = true;
    }
  }

  @use dataResource = staticDataResource(this, {
    serializedChart: () => this.sankeyChartDefinition,
    reportState: () => this.args.renderableChart.reportState,
  });

  get highchartsSeries() {
    return [this.sankeySeries] as SeriesOptionsType[];
  }

  get isChartLoading() {
    if (!this.configLoaded) {
      return true;
    }
    return this.dataResource.isLoading;
  }

  get routingOutcomes() {
    return this.finSdrConfig?.routing_outcomes || [];
  }

  get sankeyChartDefinition() {
    return {
      chart_series: SdrQualificationFunnelConfig.getChartSeriesWithLabels(
        this.intl,
        this.routingOutcomes,
      ),
      view_by: null,
    };
  }

  get highchartsOptions(): OptionsWithSankey {
    let component = this;
    return {
      xAxis: {
        visible: false,
      },
      yAxis: {
        visible: false,
      },
      tooltip: {
        enabled: false,
      },
      chart: {
        marginRight: 0,
        marginTop: 20,
        marginBottom: 50,
        animation: false,
      },
      plotOptions: {
        sankey: {
          point: {
            events: {
              mouseOver(this: Point, event: PointerEvent) {
                component.hoveredPoint = this;
                component.tooltipOpenerElement = (event.target as any).graphic.element;
                return false;
              },
              mouseOut(this: Point, _event: PointerEvent) {
                component.tooltipOpenerElement = undefined;
                return false;
              },
              click(this: Point, _event: PointerEvent) {
                component.hoveredPoint = this;
                component.onViewConversations();
                return false;
              },
            },
          },
          cursor: 'pointer',
          nodeWidth: 6,
          borderRadius: 3,
          nodePadding: 15,
          nodeAlignment: 'top',
          linkOpacity: 1.0,
          states: {
            inactive: {
              opacity: 1,
              linkOpacity: 1.0,
            },
          },
          minLinkWidth: 3,
          dataLabels: {
            padding: 0,
            color: 'black',
            useHTML: true,
            crop: false,
            overflow: 'allow',
            allowOverlap: true,
            enabled: true,
            zIndex: 10,
            nodeFormat: `
            <div class="flex flex-row items-center gap-1 text-default text-caption">
              <span class="font-mono uppercase leading-4">{point.title}</span>
              <span class="p-1 rounded text-data-detail {point.textClass}" style="background-color: {point.color}">{point.sum:,.0f}</span>
            </div>
          `,
          },
          levels: [
            {
              level: 0,
              dataLabels: {
                x: 20,
                align: 'left',
              },
            },
            {
              level: 1,
              dataLabels: {
                x: 20,
                align: 'left',
              },
            },
            {
              level: 2,
              dataLabels: {
                x: -20,
                align: 'right',
                nodeFormat: `
                <div class="flex flex-row items-center gap-1 text-default text-caption justify-end">
                  <span class="font-mono uppercase leading-4">{point.title}</span>
                  <span class="p-1 rounded text-data-detail {point.textClass}" style="background-color: {point.color}">{point.sum:,.0f}</span>
                </div>
                `,
              },
            },
          ],
        },
      },
    };
  }

  get metrics() {
    return SdrQualificationFunnelConfig.getMetricsFromChartDataRequest(
      this.dataResource.chartData,
      this.routingOutcomes,
    );
  }

  get hoveredPointDescription(): string | null {
    if (!this.hoveredPoint) {
      return null;
    }
    let name = (this.hoveredPoint as any).name;
    if (name === 'DisengagedRecovered') {
      return this.intl.t(
        'ai-agent.analyze.sales-performance.funnel-metrics.DisengagedRecoveredDescription',
      );
    }
    return null;
  }

  get hasData() {
    let topOfFunnel = this.metrics.conversations;
    return !!topOfFunnel;
  }

  get channelTypeNodes() {
    return CHANNEL_TYPES.map((channelType) => {
      let translationKey = CHANNEL_TRANSLATION_KEYS[channelType];
      return {
        id: channelType,
        title: this.intl.t(translationKey),
        findByLabel: this.intl.t(translationKey),
        count: this.metrics[channelType],
        color: SANKEY_COLORS.channelType,
        textClass: 'text-on-neutral',
      };
    });
  }

  get statusNodes() {
    let nodes: any[] = [
      {
        id: 'Qualified',
        title: this.intl.t('ai-agent.analyze.sales-performance.funnel-metrics.Qualified'),
        findByLabel: STATUS_SERIES_LOOKUP_KEYS['Qualified'],
        count: this.metrics.Qualified,
        color: SANKEY_COLORS.sdrParticipated,
        textClass: 'text-on-neutral',
      },
      {
        id: 'DisengagedRecovered',
        title: this.intl.t('ai-agent.analyze.sales-performance.funnel-metrics.DisengagedRecovered'),
        findByLabel: STATUS_SERIES_LOOKUP_KEYS['DisengagedRecovered'],
        count: this.metrics.DisengagedRecovered,
        color: SANKEY_COLORS.recovery,
        textClass: 'text-on-neutral',
      },
    ];

    let disqualifiedOutcome = this.routingOutcomes.find(
      (o) => o.identifier.toLowerCase() === 'disqualified',
    );
    if (disqualifiedOutcome) {
      nodes.push({
        id: disqualifiedOutcome.identifier,
        title: disqualifiedOutcome.title,
        findByLabel: disqualifiedOutcome.title,
        count: this.metrics[disqualifiedOutcome.identifier],
        color: SANKEY_COLORS.disqualified,
        textClass: 'text-on-neutral',
      });
    }

    nodes.push({
      id: 'Disengaged',
      title: this.intl.t('ai-agent.analyze.sales-performance.funnel-metrics.Disengaged'),
      findByLabel: STATUS_SERIES_LOOKUP_KEYS['Disengaged'],
      count: this.metrics.Disengaged,
      color: SANKEY_COLORS.deadEnd,
      textClass: 'text-on-neutral',
    });

    let escalateOutcome = this.routingOutcomes.find(
      (o) => o.identifier.toLowerCase() === 'escalate_to_support',
    );
    if (escalateOutcome) {
      nodes.push({
        id: escalateOutcome.identifier,
        title: this.intl.t('ai-agent.analyze.sales-performance.funnel-metrics.Escalated'),
        findByLabel: escalateOutcome.title,
        count: this.metrics[escalateOutcome.identifier],
        color: SANKEY_COLORS.escalated,
        textClass: 'text-on-neutral',
      });
    }

    return nodes;
  }

  get routingOutcomeNodes() {
    return this.routingOutcomes
      .filter((o) => {
        let id = o.identifier.toLowerCase();
        return id !== 'escalate_to_support' && id !== 'disqualified';
      })
      .map((outcome, index) => ({
        id: outcome.identifier,
        title: outcome.title,
        findByLabel: outcome.title,
        count: this.metrics[outcome.identifier],
        color: OUTCOME_COLORS[index % OUTCOME_COLORS.length],
        textClass: 'text-on-neutral',
      }));
  }

  get finalOutcomeNodes() {
    return [
      {
        id: 'CapturedContact',
        title: this.intl.t('ai-agent.analyze.sales-performance.funnel-metrics.CapturedContact'),
        findByLabel: this.intl.t(
          'ai-agent.analyze.sales-performance.funnel-metrics.CapturedContact',
        ),
        count: this.metrics.CapturedContact,
        color: SANKEY_COLORS.capturedContact,
        textClass: 'text-default',
      },
      {
        id: 'NoContact',
        title: this.intl.t('ai-agent.analyze.sales-performance.funnel-metrics.NoContact'),
        findByLabel: this.intl.t('ai-agent.analyze.sales-performance.funnel-metrics.NoContact'),
        count: this.metrics.NoContact,
        color: SANKEY_COLORS.default,
        textClass: 'text-default',
      },
    ];
  }

  get escalateSplitNodes() {
    let hasEscalate = this.routingOutcomes.some(
      (o) => o.identifier.toLowerCase() === 'escalate_to_support',
    );
    if (!hasEscalate) {
      return [];
    }

    return [
      {
        id: 'HandledByServiceAgent',
        title: this.intl.t(
          'ai-agent.analyze.sales-performance.funnel-metrics.HandledByServiceAgent',
        ),
        findByLabel: this.intl.t(
          'ai-agent.analyze.sales-performance.funnel-metrics.HandledByServiceAgent',
        ),
        count: this.metrics.escalate_to_supportAndHandledByServiceAgent,
        color: PALETTE['vis-azure-50'],
        textClass: 'text-on-neutral',
      },
      {
        id: 'HandledByTeammates',
        title: this.intl.t('ai-agent.analyze.sales-performance.funnel-metrics.HandledByTeammates'),
        findByLabel: this.intl.t(
          'ai-agent.analyze.sales-performance.funnel-metrics.HandledByTeammates',
        ),
        count: this.metrics.escalate_to_supportAndHandledByTeammates,
        color: PALETTE['vis-teal-50'],
        textClass: 'text-on-neutral',
      },
    ];
  }

  get escalateSplitLinks() {
    let escalateOutcome = this.routingOutcomes.find(
      (o) => o.identifier.toLowerCase() === 'escalate_to_support',
    );
    if (!escalateOutcome) {
      return [];
    }

    let serviceAgentLabel = this.intl.t(
      'ai-agent.analyze.sales-performance.funnel-metrics.HandledByServiceAgent',
    );
    let teammatesLabel = this.intl.t(
      'ai-agent.analyze.sales-performance.funnel-metrics.HandledByTeammates',
    );

    return [
      {
        from: escalateOutcome.identifier,
        to: 'HandledByServiceAgent',
        name: `${escalateOutcome.title} → ${serviceAgentLabel}`,
        findByLabel: `${escalateOutcome.title} & ${serviceAgentLabel}`,
        weight: this.metrics.escalate_to_supportAndHandledByServiceAgent || 0,
      },
      {
        from: escalateOutcome.identifier,
        to: 'HandledByTeammates',
        name: `${escalateOutcome.title} → ${teammatesLabel}`,
        findByLabel: `${escalateOutcome.title} & ${teammatesLabel}`,
        weight: this.metrics.escalate_to_supportAndHandledByTeammates || 0,
      },
    ];
  }

  get channelToStatusLinks() {
    let qualifiedLabel = this.intl.t('ai-agent.analyze.sales-performance.funnel-metrics.Qualified');
    let disengagedRecoveredLabel = this.intl.t(
      'ai-agent.analyze.sales-performance.funnel-metrics.DisengagedRecovered',
    );
    let disengagedLabel = this.intl.t(
      'ai-agent.analyze.sales-performance.funnel-metrics.Disengaged',
    );

    let escalateOutcome = this.routingOutcomes.find(
      (o) => o.identifier.toLowerCase() === 'escalate_to_support',
    );
    let disqualifiedOutcome = this.routingOutcomes.find(
      (o) => o.identifier.toLowerCase() === 'disqualified',
    );

    let links: any[] = [];

    CHANNEL_TYPES.forEach((channel) => {
      let translationKey = CHANNEL_TRANSLATION_KEYS[channel];
      let channelLabel = this.intl.t(translationKey);
      links.push(
        {
          from: channel,
          to: 'Qualified',
          name: `${channel} → Qualified`,
          findByLabel: `${channelLabel} & ${qualifiedLabel}`,
          weight: this.metrics[`${channel}AndQualified`] || 0,
        },
        {
          from: channel,
          to: 'DisengagedRecovered',
          name: `${channel} → DisengagedRecovered`,
          findByLabel: `${channelLabel} & ${disengagedRecoveredLabel}`,
          weight: this.metrics[`${channel}AndDisengagedRecovered`] || 0,
        },
      );
      if (disqualifiedOutcome) {
        links.push({
          from: channel,
          to: disqualifiedOutcome.identifier,
          name: `${channel} → ${disqualifiedOutcome.title}`,
          findByLabel: `${channelLabel} & ${disqualifiedOutcome.title}`,
          weight: this.metrics[`${channel}And${disqualifiedOutcome.identifier}`] || 0,
        });
      }
      links.push({
        from: channel,
        to: 'Disengaged',
        name: `${channel} → Disengaged`,
        findByLabel: `${channelLabel} & ${disengagedLabel}`,
        weight: this.metrics[`${channel}AndDisengaged`] || 0,
      });
      if (escalateOutcome) {
        links.push({
          from: channel,
          to: escalateOutcome.identifier,
          name: `${channel} → ${escalateOutcome.title}`,
          findByLabel: `${channelLabel} & ${escalateOutcome.title}`,
          weight: this.metrics[`${channel}And${escalateOutcome.identifier}`] || 0,
        });
      }
    });

    return links;
  }

  get statusToRoutingOutcomeLinks() {
    let qualifiedLabel = this.intl.t('ai-agent.analyze.sales-performance.funnel-metrics.Qualified');
    let disengagedRecoveredLabel = this.intl.t(
      'ai-agent.analyze.sales-performance.funnel-metrics.DisengagedRecovered',
    );

    let links: any[] = [];

    this.routingOutcomes
      .filter((o) => {
        let id = o.identifier.toLowerCase();
        return id !== 'escalate_to_support' && id !== 'disqualified';
      })
      .forEach((outcome) => {
        links.push(
          {
            from: 'Qualified',
            to: outcome.identifier,
            name: `Qualified → ${outcome.title}`,
            findByLabel: `${qualifiedLabel} & ${outcome.title}`,
            weight: this.metrics[`QualifiedAnd${outcome.identifier}`] || 0,
          },
          {
            from: 'DisengagedRecovered',
            to: outcome.identifier,
            name: `DisengagedRecovered → ${outcome.title}`,
            findByLabel: `${disengagedRecoveredLabel} & ${outcome.title}`,
            weight: this.metrics[`DisengagedRecoveredAnd${outcome.identifier}`] || 0,
          },
        );
      });

    return links;
  }

  get disengagedToFinalOutcomeLinks() {
    let capturedContactLabel = this.intl.t(
      'ai-agent.analyze.sales-performance.funnel-metrics.CapturedContact',
    );
    let noContactLabel = this.intl.t('ai-agent.analyze.sales-performance.funnel-metrics.NoContact');
    let disengagedLabel = this.intl.t(
      'ai-agent.analyze.sales-performance.funnel-metrics.Disengaged',
    );

    return [
      {
        from: 'Disengaged',
        to: 'CapturedContact',
        name: 'Disengaged → Contact captured',
        findByLabel: `${disengagedLabel} & ${capturedContactLabel}`,
        weight: this.metrics.DisengagedAndCapturedContact || 0,
      },
      {
        from: 'Disengaged',
        to: 'NoContact',
        name: 'Disengaged → No contact',
        findByLabel: `${disengagedLabel} & ${noContactLabel}`,
        weight: this.metrics.DisengagedAndNoContact || 0,
      },
    ];
  }

  get sankeySeries(): SeriesSankeyOptions {
    let nodes = [
      ...this.channelTypeNodes,
      ...this.statusNodes,
      ...this.routingOutcomeNodes,
      ...this.finalOutcomeNodes,
      ...this.escalateSplitNodes,
    ];
    let data = stripZeroWeights([
      ...this.channelToStatusLinks,
      ...this.statusToRoutingOutcomeLinks,
      ...this.disengagedToFinalOutcomeLinks,
      ...this.escalateSplitLinks,
    ]);

    return {
      type: 'sankey',
      linkColorMode: 'gradient',
      animation: false,
      custom: {
        total: this.metrics.conversations,
      },
      nodes,
      data,
    };
  }

  @action
  onViewConversations() {
    if (!this.hoveredPoint) {
      return;
    }

    let metricName =
      this.hoveredPoint.findByLabel || this.hoveredPoint.title || this.hoveredPoint.name;
    let chartSeries = this.args.renderableChart.chartSeries.toArray().findBy('label', metricName);

    this.args.openUnderlyingDataModal(chartSeries);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Bespoke::SdrPerformance::QualificationFunnel': typeof QualificationFunnel;
    'reporting/bespoke/sdr-performance/qualification-funnel': typeof QualificationFunnel;
  }
}
